import { useState, useEffect, useMemo, useRef } from 'react'
import { createPortal } from 'react-dom'
import { VACIO } from '../unidades/filtrosLogica'
import { currentLocale } from '../../i18n'

// A column's own filter, in its header: a small funnel that opens a window by the column's type -- a date by year and
// month (or a from/to range), a state or a short list of values as ticks, text and numbers as typed. It writes one
// filter ({ op, valor }, see unidades/filtrosLogica) or none. `valores`: what the column shows in the rows now
// (the options and the years come from there). `t`: the "engineering" translator.
const MAX_OPCIONES = 60

const vacio = (v) => v == null || String(v).trim() === ''

export default function FiltroColumna({ columna, valores, filtro, onChange, t }) {
  const [anchor, setAnchor] = useState(null)
  const activo = !!filtro
  return (
    <>
      <button type="button" data-columna-btn aria-label={t('partes.filtroColumna.abrir', { columna: columna.nombre })}
        title={t('partes.filtroColumna.abrir', { columna: columna.nombre })}
        onClick={e => { const r = e.currentTarget.getBoundingClientRect(); setAnchor(a => (a ? null : r)) }}
        className={`ml-0.5 inline-flex items-center justify-center w-5 h-5 rounded align-middle transition-colors ${
          activo ? 'bg-indigo-100 text-indigo-700' : 'text-gray-300 hover:text-gray-600 hover:bg-gray-100'}`}>
        <svg viewBox="0 0 24 24" className="w-3.5 h-3.5" fill={activo ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinejoin="round" aria-hidden="true">
          <path d="M3 4h18l-7 8.5V19l-4 2v-8.5L3 4Z" />
        </svg>
      </button>
      {anchor && (
        <Ventana anchor={anchor} onClose={() => setAnchor(null)}>
          <div className="space-y-3">
            <div className="flex items-center justify-between gap-3">
              <span className="text-xs font-semibold text-gray-700">{columna.nombre}</span>
              {activo && (
                <button type="button" onClick={() => { onChange(null); setAnchor(null) }} className="text-xs text-red-600 hover:text-red-700 font-medium">
                  {t('partes.filtroColumna.quitar')}
                </button>
              )}
            </div>
            {columna.tipo === 'fecha' && <PorFecha valores={valores} filtro={filtro} onChange={onChange} t={t} />}
            {columna.tipo === 'numero' && <PorNumero filtro={filtro} onChange={onChange} t={t} />}
            {columna.tipo === 'archivo' && <PorArchivo filtro={filtro} onChange={onChange} t={t} />}
            {(columna.tipo === 'estado' || columna.tipo === 'lista') && <PorLista valores={valores} filtro={filtro} onChange={onChange} t={t} />}
            {(columna.tipo === 'texto' || columna.tipo === 'persona') && <PorTexto filtro={filtro} onChange={onChange} t={t} />}
          </div>
        </Ventana>
      )}
    </>
  )
}

// The window, drawn over the page so the table's own scrolling never cuts it.
function Ventana({ anchor, onClose, children }) {
  const ref = useRef(null)
  useEffect(() => {
    const fuera = (e) => { if (ref.current && !ref.current.contains(e.target) && !e.target.closest('[data-columna-btn]')) onClose() }
    const tecla = (e) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', fuera)
    window.addEventListener('keydown', tecla)
    return () => { document.removeEventListener('mousedown', fuera); window.removeEventListener('keydown', tecla) }
  }, [onClose])
  const left = Math.max(8, Math.min(anchor.left, window.innerWidth - 288))
  return createPortal(
    <div ref={ref} style={{ position: 'fixed', left, top: anchor.bottom + 4, width: 272, zIndex: 60 }}
      className="bg-white border border-gray-200 rounded-xl shadow-lg p-3 text-sm font-normal text-gray-800">
      {children}
    </div>, document.body)
}

const inp = 'w-full border border-gray-200 rounded-md px-2 py-1.5 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-gray-200'

function PorTexto({ filtro, onChange, t }) {
  const [v, setV] = useState(filtro?.op === 'contains' ? filtro.valor : '')
  return (
    <input autoFocus className={inp} value={v} placeholder={t('partes.filtroColumna.contiene')}
      onChange={e => { setV(e.target.value); onChange(e.target.value.trim() ? { op: 'contains', valor: e.target.value } : null) }} />
  )
}

function PorNumero({ filtro, onChange, t }) {
  const [min, max] = filtro?.op === 'nbetween' ? filtro.valor.split('|') : ['', '']
  const poner = (a, b) => onChange(vacio(a) && vacio(b) ? null : { op: 'nbetween', valor: `${a || ''}|${b || ''}` })
  return (
    <div className="grid grid-cols-2 gap-2">
      <label className="text-xs text-gray-500">{t('partes.filtroColumna.desdeNum')}
        <input type="number" className={inp} defaultValue={min} onChange={e => poner(e.target.value, max)} /></label>
      <label className="text-xs text-gray-500">{t('partes.filtroColumna.hastaNum')}
        <input type="number" className={inp} defaultValue={max} onChange={e => poner(min, e.target.value)} /></label>
    </div>
  )
}

function PorArchivo({ filtro, onChange, t }) {
  return (
    <div className="flex gap-2">
      {[['not_empty', 'conArchivos'], ['empty', 'sinArchivos']].map(([op, k]) => (
        <button key={op} type="button" aria-pressed={filtro?.op === op} onClick={() => onChange(filtro?.op === op ? null : { op, valor: '' })}
          className={`flex-1 px-2 py-1.5 rounded-md border text-xs font-medium ${filtro?.op === op ? 'bg-gray-900 text-white border-gray-900' : 'border-gray-200 text-gray-700 hover:bg-gray-50'}`}>
          {t(`partes.filtroColumna.${k}`)}
        </button>
      ))}
    </div>
  )
}

// A state or a short list: tick the values to keep (the empty cells are one more value).
function PorLista({ valores, filtro, onChange, t }) {
  const opciones = useMemo(() => {
    const cuenta = new Map()
    for (const v of valores) {
      const k = vacio(v) ? VACIO : String(v)
      cuenta.set(k, (cuenta.get(k) || 0) + 1)
    }
    return [...cuenta.entries()].sort((a, b) => (a[0] === VACIO) - (b[0] === VACIO) || a[0].localeCompare(b[0], undefined, { numeric: true }))
  }, [valores])
  const marcados = new Set(filtro?.op === 'in' ? filtro.valor.split('|') : opciones.map(([k]) => k))
  const poner = (set) => onChange(set.size === opciones.length || set.size === 0 ? null : { op: 'in', valor: [...set].join('|') })
  const alternar = (k) => { const n = new Set(marcados); if (n.has(k)) n.delete(k); else n.add(k); poner(n) }
  if (opciones.length > MAX_OPCIONES) return <PorTexto filtro={filtro?.op === 'contains' ? filtro : null} onChange={onChange} t={t} />
  return (
    <div className="max-h-60 overflow-y-auto space-y-0.5">
      {opciones.map(([k, n]) => (
        <label key={k} className="flex items-center gap-2 px-1 py-1 rounded hover:bg-gray-50 cursor-pointer">
          <input type="checkbox" className="accent-gray-900" checked={marcados.has(k)} onChange={() => alternar(k)} />
          <span className={`flex-1 truncate ${k === VACIO ? 'text-gray-400 italic' : ''}`}>{k === VACIO ? t('partes.filtroColumna.vacio') : k}</span>
          <span className="text-xs text-gray-400 tabular-nums">{n}</span>
        </label>
      ))}
    </div>
  )
}

// A date: a year, then a month of it -- or a from/to range.
function PorFecha({ valores, filtro, onChange, t }) {
  const años = useMemo(() => [...new Set(valores.filter(v => !vacio(v)).map(v => String(v).slice(0, 4)))].sort().reverse(), [valores])
  const op = filtro?.op
  const [año, setAño] = useState(op === 'year' ? filtro.valor : op === 'month' ? filtro.valor.slice(0, 4) : '')
  const [mes, setMes] = useState(op === 'month' ? filtro.valor.slice(5, 7) : '')
  const [desde, hasta] = op === 'between' ? filtro.valor.split('|') : ['', '']
  const meses = useMemo(() => Array.from({ length: 12 }, (_, i) => ({
    n: String(i + 1).padStart(2, '0'),
    nombre: new Date(2000, i, 1).toLocaleDateString(currentLocale(), { month: 'long' }),
  })), [])
  const poner = (a, m) => {
    setAño(a); setMes(m)
    onChange(!a ? null : m ? { op: 'month', valor: `${a}-${m}` } : { op: 'year', valor: a })
  }
  const rango = (d, h) => {
    setAño(''); setMes('')
    onChange(!d && !h ? null : { op: 'between', valor: `${d || ''}|${h || ''}` })
  }
  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <label className="text-xs text-gray-500">{t('partes.filtroColumna.año')}
          <select className={inp} value={año} onChange={e => poner(e.target.value, e.target.value ? mes : '')}>
            <option value="">{t('partes.filtroColumna.todos')}</option>
            {años.map(a => <option key={a} value={a}>{a}</option>)}
          </select></label>
        <label className="text-xs text-gray-500">{t('partes.filtroColumna.mes')}
          <select className={inp} value={mes} disabled={!año} onChange={e => poner(año, e.target.value)}>
            <option value="">{t('partes.filtroColumna.todos')}</option>
            {meses.map(m => <option key={m.n} value={m.n}>{m.nombre}</option>)}
          </select></label>
      </div>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-gray-400 mb-1">{t('partes.filtroColumna.rango')}</div>
        <div className="grid grid-cols-2 gap-2">
          <label className="text-xs text-gray-500">{t('partes.filtroColumna.desde')}
            <input type="date" className={inp} value={desde} onChange={e => rango(e.target.value, hasta)} /></label>
          <label className="text-xs text-gray-500">{t('partes.filtroColumna.hasta')}
            <input type="date" className={inp} value={hasta} onChange={e => rango(desde, e.target.value)} /></label>
        </div>
      </div>
    </div>
  )
}
